import { getLoaderMorph } from "./loaderMorphUtility";

// The loader index.html shows before the app's JavaScript has arrived, so a slow connection
// doesn't look at an empty page (vite.config.ts writes it in). It plays the same morph as pure CSS
// animations, which run from the first paint with no JavaScript, in Planny's colours: the studio's
// palette isn't known yet. The app takes over from it: the first <Loading /> carries on from its
// frame and removes it, and ApiHelperAppContextProvider removes it once nothing is loading.

export const BOOT_LOADER_ID = "boot-loader";
const BOOT_LOADER_PLACEHOLDER = "<!-- boot-loader -->";
const SHAPE_CLASS = "boot-loader-shape";

// Planny's purple and pink. <Loading /> starts from them too when it takes over, so the colours
// blend into the studio's palette instead of switching in one frame.
export const BOOT_LOADER_COLOURS = { from: "#7877c6", to: "#ff77c6" };
// The same look as Loading.module.scss ($loader_frostedMask, $loaderSize, $whiteGlass_textMuted),
// inline because no stylesheet of the app has loaded yet.
const MASK = "rgba(24, 23, 42, 0.55)";
const SIZE = 56;

// Runs inline, so it works even when the app's JavaScript never arrives: if a script fails to load
// or throws before the app has taken over, the loader goes, instead of spinning over a dead page.
// A broken image is no reason to.
const FAILSAFE = `addEventListener("error",function onError(event){if(!(event instanceof ErrorEvent)&&!(event.target&&event.target.tagName==="SCRIPT"))return;removeEventListener("error",onError,true);var loader=document.getElementById("${BOOT_LOADER_ID}");if(loader)loader.remove()},true)`;

// A @keyframes rule from the morph's Web Animations keyframes, eased between every two of them.
const keyframesRule = (name: string, keyframes: Keyframe[], declaration: (keyframe: Keyframe) => string) => {
  const stops = keyframes.map(
    (keyframe, index) =>
      `${(index * 100) / (keyframes.length - 1)}%{${declaration(keyframe)};animation-timing-function:${keyframe.easing}}`
  );
  return `@keyframes ${name}{${stops.join("")}}`;
};

// The <style> sits inside the loader, so removing the loader removes it too. The failsafe's
// listener stays until the first error, and then finds nothing to remove.
export const bootLoaderHtml = () => {
  const morph = getLoaderMorph();
  const animation = (name: string, pace: number) =>
    `${name} ${morph.cycleMilliseconds * pace}ms linear -${morph.leadInMilliseconds * pace}ms infinite`;

  const style = [
    `#${BOOT_LOADER_ID}{position:fixed;inset:0;z-index:100;display:flex;flex-direction:column;align-items:center;justify-content:center;background-color:${MASK};-webkit-backdrop-filter:blur(6px);backdrop-filter:blur(6px)}`,
    `#${BOOT_LOADER_ID} .boot-loader-turn{width:${SIZE}px;height:${SIZE}px;animation:${animation("bootLoaderTurn", 1)}}`,
    `#${BOOT_LOADER_ID} .${SHAPE_CLASS}{width:100%;height:100%;background:linear-gradient(135deg,${BOOT_LOADER_COLOURS.from},${BOOT_LOADER_COLOURS.to});clip-path:${morph.shapes[0]};animation:${animation("bootLoaderMorph", 1)}}`,
    `#${BOOT_LOADER_ID} .boot-loader-text{margin-top:22px;font-size:14px;font-weight:500;letter-spacing:0.06em;color:rgba(255,255,255,0.8)}`,
    // Less motion asked for: the shape still moves (a frozen loader reads as a hung page), only at half the pace.
    `@media (prefers-reduced-motion:reduce){#${BOOT_LOADER_ID} .boot-loader-turn{animation:${animation("bootLoaderTurn", 2)}}#${BOOT_LOADER_ID} .${SHAPE_CLASS}{animation:${animation("bootLoaderMorph", 2)}}}`,
    keyframesRule("bootLoaderMorph", morph.morphKeyframes, (keyframe) => `clip-path:${keyframe.clipPath}`),
    keyframesRule("bootLoaderTurn", morph.turnKeyframes, (keyframe) => `transform:${keyframe.transform}`),
  ].join("\n");

  return `<div id="${BOOT_LOADER_ID}" role="status">
      <style>
${style}
      </style>
      <script>${FAILSAFE}</script>
      <div class="boot-loader-turn" aria-hidden="true"><div class="${SHAPE_CLASS}"></div></div>
      <div class="boot-loader-text">Зареждане...</div>
    </div>`;
};

// What vite.config.ts does to index.html.
export const withBootLoader = (html: string) => html.replace(BOOT_LOADER_PLACEHOLDER, bootLoaderHtml());

// Where the boot loader's morph is now (milliseconds into its cycle at the normal pace), or
// undefined once it is gone.
export const bootLoaderMorphPosition = () => {
  const shape = document.getElementById(BOOT_LOADER_ID)?.querySelector(`.${SHAPE_CLASS}`);
  const progress = shape?.getAnimations?.()[0]?.effect?.getComputedTiming().progress;

  return typeof progress === "number" ? progress * getLoaderMorph().cycleMilliseconds : undefined;
};

// Once the app has rendered, the page under the boot loader is real: taps go through to it.
export const letTapsThroughBootLoader = () => {
  const loader = document.getElementById(BOOT_LOADER_ID);
  if (loader) loader.style.pointerEvents = "none";
};

export const removeBootLoader = () => document.getElementById(BOOT_LOADER_ID)?.remove();
